<script setup>
/* Card do formulário da tela "Simulação e Digitação".
   Layout fiel ao MAZAM AI (simulation-form.tsx); o botão ENRIQUECER usa o
   mesmo endpoint e a MESMA regra de telefone já validada em produção no
   SimuladorClt/SimuladorModal.vue — nada foi reinventado aqui.
   v42: SIMULAR emite pro Index, que cria a simulação no simulador-api. */
import { computed, onMounted, ref, watch } from 'vue';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import cltApi from 'dashboard/api/cltSimulador';
import { PRODUTOS } from '../bancos.js';

const props = defineProps({
  // true enquanto o Index cria a simulação (POST) — trava o botão Simular
  simulando: { type: Boolean, default: false },
});

const emit = defineEmits(['enriquecido', 'simular', 'caixa']);

const store = useStore();
// v49 (creds por caixa): simulador-api agora EXIGE inbox_id (422 sem) — a
// credencial de cada banco e resolvida pela caixa (tenant = inbox). Mesmo
// padrao de leitura de inboxes do Meta -> Disparo (DisparoCentral.vue).
const inboxesGetter = useMapGetter('inboxes/getInboxes');
const inboxId = ref('');
const caixaOk = computed(() => !!inboxId.value);

// v50: assim que a caixa é escolhida (ou pré-selecionada), avisa o Index para
// já desenhar os cards dos bancos DAQUELA caixa como "Aguardando" — antes eles
// só apareciam depois de clicar Simular, e o operador achava que a caixa não
// tinha banco. `immediate` cobre a pré-seleção de caixa única.
watch(inboxId, id => emit('caixa', id), { immediate: true });

onMounted(async () => {
  try {
    await store.dispatch('inboxes/get');
  } catch (e) {
    // se a lista falhar, o select fica vazio e o botão Simular continua
    // bloqueado (caixaOk = false) — não derruba a tela.
  }
  // uma única caixa disponível: pré-seleciona (não há ambiguidade a resolver)
  if (!inboxId.value && (inboxesGetter.value || []).length === 1) {
    inboxId.value = inboxesGetter.value[0].id;
  }
});

const produto = ref('clt');
const cpf = ref('');
const celular = ref('');
const nascimento = ref(''); // yyyy-mm-dd
const nome = ref('');
const enrich = ref({});
const busy = ref(''); // guarda o NOME da ação em curso (convenção do projeto)
const erroGeral = ref('');

const onlyDigits = s => (s || '').replace(/\D+/g, '');
const cpfOk = computed(() => cpf.value.length === 11);

/* Máscara de exibição do CPF: o valor guardado é sempre só dígitos. */
const cpfMasked = computed(() => {
  const d = cpf.value;
  if (!d) return '';
  return d
    .replace(/^(\d{3})(\d)/, '$1.$2')
    .replace(/^(\d{3})\.(\d{3})(\d)/, '$1.$2.$3')
    .replace(/\.(\d{3})(\d{1,2})$/, '.$1-$2');
});
const onCpfInput = e => {
  cpf.value = onlyDigits(e.target.value).slice(0, 11);
  e.target.value = cpfMasked.value;
};

/* Normaliza o que o operador digita, MESMA regra do backend
   (clt_bancos/utils.rb#normalize_phone_br): tira o +55 e insere o 9º dígito. */
const normalizaFoneBr = valor => {
  let d = onlyDigits(valor || '');
  if ((d.length === 12 || d.length === 13) && d.startsWith('55')) d = d.slice(2);
  if (d.length === 10) d = `${d.slice(0, 2)}9${d.slice(2)}`;
  return d;
};
const arrumarCelular = () => {
  const d = normalizaFoneBr(celular.value);
  if (d) celular.value = d;
};
const celularSuspeito = computed(() => {
  const d = onlyDigits(celular.value || '');
  return d.length > 0 && d.length !== 11;
});

/* De onde veio o celular preenchido — o operador precisa saber o que discar. */
const origemFone = ref('');
const FONE_SELO = {
  contato: {
    texto: 'do contato',
    classe: 'bg-n-teal-3 text-n-teal-11',
    ajuda: 'Número do contato já existente no Chatwoot',
  },
  consulta: {
    texto: 'da consulta',
    classe: 'bg-n-amber-3 text-n-amber-11',
    ajuda: 'Número veio da higienização (CPF não é contato do Chatwoot)',
  },
  nenhum: {
    texto: 'sem telefone na consulta',
    classe: 'bg-n-ruby-3 text-n-ruby-11',
    ajuda:
      'Nem o Chatwoot nem a higienização têm telefone deste CPF — digite o número.',
  },
};

const enriquecer = async () => {
  if (!cpfOk.value) return;
  busy.value = 'enriquecer';
  erroGeral.value = '';
  try {
    const { data } = await cltApi.get('enriquecer', { cpf: cpf.value });
    enrich.value = data || {};
    if (data.name) nome.value = data.name;
    if (data.birthDate) nascimento.value = data.birthDate.slice(0, 10);
    // Telefone: o do contato do Chatwoot manda (é por onde já se falou com o
    // cliente). Consulta avulsa não tem esse número: aí vale o da higienização.
    const foneChatwoot = normalizaFoneBr(data.chatwootPhone);
    const foneConsulta = normalizaFoneBr(
      `${data.phoneRegionCode || ''}${data.phone || ''}`
    );
    if (foneChatwoot) {
      celular.value = foneChatwoot;
      origemFone.value = 'contato';
    } else if (foneConsulta) {
      celular.value = foneConsulta;
      origemFone.value = 'consulta';
    } else {
      origemFone.value = 'nenhum';
    }
    emit('enriquecido', {
      cpf: cpf.value,
      nome: nome.value,
      celular: celular.value,
      nascimento: nascimento.value,
      produto: produto.value,
      dados: enrich.value,
    });
    useAlert('Dados enriquecidos com sucesso');
  } catch (e) {
    erroGeral.value = `Falha ao enriquecer: ${e.response?.data?.error || e.message}`;
  } finally {
    busy.value = '';
  }
};

/* v42: o Index cria a simulação no simulador-api e cuida do polling. */
const simular = () => {
  if (!cpfOk.value || !caixaOk.value || props.simulando) return;
  erroGeral.value = '';
  emit('simular', {
    cpf: cpf.value,
    nome: nome.value,
    celular: celular.value,
    nascimento: nascimento.value,
    produto: produto.value,
    enriquecimento: enrich.value,
    // v49: obrigatório no simulador-api — credencial do banco é por caixa
    inbox_id: inboxId.value,
  });
};

/* Limpar zera SÓ o formulário (decisão do Kairo): a lista e os cards ficam. */
const limpar = () => {
  cpf.value = '';
  celular.value = '';
  nascimento.value = '';
  nome.value = '';
  enrich.value = {};
  origemFone.value = '';
  erroGeral.value = '';
};

/* v42: Editar no card do cliente / clicar numa simulação salva repõe a ficha. */
const preencher = dados => {
  cpf.value = onlyDigits(dados?.cpf).slice(0, 11);
  nome.value = dados?.nome || '';
  celular.value = onlyDigits(dados?.celular);
  nascimento.value = dados?.nascimento || '';
  if (dados?.produto) produto.value = dados.produto;
  origemFone.value = '';
  erroGeral.value = '';
};

const mostrarErro = msg => {
  erroGeral.value = msg;
};

defineExpose({ limpar, preencher, mostrarErro });
</script>

<template>
  <div class="p-6 border rounded-lg border-n-weak bg-n-solid-2">
    <p
      v-if="erroGeral"
      class="p-2 mb-3 text-sm border rounded-lg border-n-ruby-7 bg-n-ruby-2 text-n-ruby-11"
    >
      {{ erroGeral }}
    </p>

    <div class="flex flex-col gap-4">
      <!-- Produto + Link de afiliado -->
      <div class="flex items-start justify-between gap-2">
        <div class="flex flex-col w-full gap-1.5 max-w-xs">
          <label class="sd-label">Produto</label>
          <select v-model="produto" class="sd-input">
            <option v-for="p in PRODUTOS" :key="p.value" :value="p.value">
              {{ p.label }}
            </option>
          </select>
        </div>
        <button
          type="button"
          class="inline-flex items-center gap-1.5 mt-1 text-xs transition-colors shrink-0 text-n-slate-11 hover:text-n-slate-12"
        >
          <span class="i-lucide-link-2 size-3.5" />
          Link de afiliado
        </button>
      </div>

      <!-- v49: caixa de entrada — define de qual inbox vem a credencial
           bancária usada nesta simulação (creds por caixa). -->
      <div class="flex flex-col gap-1.5">
        <label class="sd-label">Caixa de entrada</label>
        <select v-model="inboxId" class="sd-input">
          <option value="" disabled>
            {{
              (inboxesGetter || []).length
                ? 'Selecione a caixa'
                : 'Nenhuma caixa disponível'
            }}
          </option>
          <option
            v-for="inbox in inboxesGetter"
            :key="inbox.id"
            :value="inbox.id"
          >
            {{ inbox.name }}
          </option>
        </select>
      </div>

      <div class="flex flex-col gap-1.5">
        <label class="sd-label">CPF</label>
        <input
          :value="cpfMasked"
          type="text"
          inputmode="numeric"
          autocomplete="off"
          placeholder="000.000.000-00"
          class="sd-input tabular-nums"
          @input="onCpfInput"
        />
      </div>

      <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div class="flex flex-col gap-1.5">
          <label class="sd-label">
            DDD + celular
            <span
              v-if="origemFone"
              class="ml-1 px-1.5 py-0.5 rounded text-[10px] font-medium"
              :class="FONE_SELO[origemFone].classe"
              :title="FONE_SELO[origemFone].ajuda"
            >
              {{ FONE_SELO[origemFone].texto }}
            </span>
          </label>
          <input
            v-model="celular"
            type="text"
            placeholder="(11) 98765-4321"
            class="sd-input tabular-nums"
            @blur="arrumarCelular"
          />
          <span v-if="celularSuspeito" class="text-[10px] text-n-amber-11">
            Esperado DDD + 9 dígitos (ex.: 51989304770).
          </span>
        </div>
        <div class="flex flex-col gap-1.5">
          <label class="sd-label">Data de Nascimento</label>
          <input
            v-model="nascimento"
            type="date"
            placeholder="dd/mm/aaaa"
            class="sd-input tabular-nums"
          />
        </div>
      </div>

      <div class="flex flex-col gap-1.5">
        <label class="sd-label">Nome completo</label>
        <input
          v-model="nome"
          type="text"
          placeholder="Como no documento"
          class="sd-input"
        />
      </div>

      <!-- Ações: Limpar → Enriquecer (amarelo) → Simular -->
      <div class="flex flex-wrap items-center justify-end gap-3">
        <span v-if="!cpfOk" class="text-xs text-n-slate-11">
          Informe um CPF válido para simular.
        </span>
        <span v-else-if="!caixaOk" class="text-xs text-n-slate-11">
          Selecione a caixa de entrada para simular.
        </span>
        <button type="button" class="sd-btn-ghost" @click="limpar">
          Limpar
        </button>
        <button
          type="button"
          class="sd-btn-amarelo"
          :disabled="!cpfOk || busy === 'enriquecer'"
          @click="enriquecer"
        >
          <span
            :class="busy === 'enriquecer' ? 'i-lucide-loader-2 animate-spin' : 'i-lucide-user-plus'"
            class="size-4"
          />
          {{ busy === 'enriquecer' ? 'Enriquecendo…' : 'Enriquecer' }}
        </button>
        <button
          type="button"
          class="sd-btn-primary"
          :disabled="!cpfOk || !caixaOk || simulando"
          @click="simular"
        >
          <span
            v-if="simulando"
            class="i-lucide-loader-2 size-4 animate-spin"
          />
          {{ simulando ? 'Simulando…' : 'Simular' }}
        </button>
      </div>
    </div>
  </div>
</template>
